import { ArrowLeft, FileText } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/eh8zi2sq_logo.svg";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="terms-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="terms-nav-logo">
              <img src={LOGO_URL} alt="GospelTube" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="terms-back-btn"
            >
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-xl bg-brand/20 text-brand">
              <FileText size={28} />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white">Terms of Service</h1>
              <p className="text-gray-500 text-sm">Effective Date: January 30, 2026 | Last Updated: January 30, 2026</p>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 md:p-12 space-y-8">
              
              <p className="text-gray-400 leading-relaxed">
                Welcome to GospelTube ("GospelTube," "we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our website, mobile and TV applications, and related services (collectively, the "Service").
              </p>
              <p className="text-gray-400 leading-relaxed">
                By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
              </p>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">1) Who We Are</h2>
                <ul className="text-gray-400 space-y-2">
                  <li><strong className="text-white">Legal Entity:</strong> GospelTube Inc</li>
                  <li><strong className="text-white">Location:</strong> New York, USA</li>
                  <li><strong className="text-white">Support/Contact:</strong> <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a></li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">2) Eligibility</h2>
                <p className="text-gray-400 leading-relaxed">
                  You must be at least 13 years old to use the Service. If you are under 18, you may use the Service only with the consent and supervision of a parent or legal guardian.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">3) Accounts and Security</h2>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>You may need an account to access features (including streaming, uploads, comments, or donations).</li>
                  <li>You are responsible for all activity under your account and for keeping your credentials secure.</li>
                  <li>You agree to provide accurate information and keep it updated.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">4) The Service</h2>
                <p className="text-gray-400 leading-relaxed">
                  GospelTube is a faith and purpose streaming and community platform that may include churches, music ministers, creators/bloggers, mental health advocates, charities, and uplifting/values-based movies and video content. The Service may be available on iOS, Android, web browsers, and smart TVs.
                </p>
                <p className="text-gray-400 leading-relaxed mt-4">
                  We may modify, suspend, or discontinue the Service (or any portion) at any time.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">5) User Content and License</h2>
                <p className="text-gray-400 leading-relaxed">
                  "User Content" means any content you submit, upload, stream, post, or otherwise make available through the Service (including video, audio, text, images, comments, and metadata).
                </p>
                <p className="text-gray-400 leading-relaxed mt-4">
                  You retain ownership of your User Content. However, by providing User Content, you grant GospelTube a non-exclusive, worldwide, royalty-free, sublicensable, transferable license to host, store, reproduce, encode, modify (for formatting/technical delivery), distribute, publicly perform, publicly display, and make available your User Content solely to operate, improve, market, and provide the Service.
                </p>
                <p className="text-gray-400 leading-relaxed mt-4">You represent and warrant that:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                  <li>You own or have all necessary rights and permissions to your User Content; and</li>
                  <li>Your User Content does not violate these Terms, our policies, or any law.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">6) Creator / Publisher Responsibilities (Including Movies)</h2>
                <p className="text-gray-400 leading-relaxed">
                  If you upload or distribute content (including films/movies), you agree that you have obtained all rights, licenses, and clearances needed (e.g., music, footage, performances, trademarks, likeness rights) and that your content complies with our Content Policy and applicable law. You must provide accurate titles, descriptions, and any required age/rating information where applicable.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">7) Content Policy and Enforcement</h2>
                <p className="text-gray-400 leading-relaxed">
                  Our Content Policy & Community Guidelines are part of these Terms. We may remove content, restrict visibility, suspend accounts, or terminate access for violations, at our discretion. We may also take action to protect users, the Service, and our legal obligations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">8) Prohibited Conduct</h2>
                <p className="text-gray-400 leading-relaxed">You agree not to:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                  <li>Infringe intellectual property, privacy, or other rights</li>
                  <li>Upload illegal, harmful, hateful, harassing, sexually exploitative, or violent content</li>
                  <li>Post scams, spam, malware, or attempt unauthorized access</li>
                  <li>Impersonate others or misrepresent affiliation</li>
                  <li>Circumvent enforcement actions or create accounts to evade bans</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">9) Payments, Subscriptions, and Donations (If Applicable)</h2>
                <p className="text-gray-400 leading-relaxed">
                  GospelTube may offer paid features including subscriptions and donations/tips to churches, charities, and creators. Payment processing may be handled by third parties, and their terms may apply.
                </p>
                <h3 className="text-xl font-medium text-white mt-6 mb-3">Refund Policy (Summary):</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Digital purchases are non-refundable once access is provided, except where required by law.</li>
                  <li>Subscriptions can be canceled anytime to stop future charges, and you keep access until the end of the current billing period.</li>
                  <li>Donations/tips are non-refundable, except for proven fraud or duplicate charges.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">10) Third-Party Services</h2>
                <p className="text-gray-400 leading-relaxed">
                  The Service may link to or integrate with third-party services. We are not responsible for third-party content, policies, or practices.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">11) Copyright (DMCA)</h2>
                <p className="text-gray-400 leading-relaxed">
                  We respect intellectual property rights. See our <Link to="/dmca" className="text-brand hover:underline">Copyright/DMCA Policy</Link> for notices and counter-notices. We may terminate accounts of repeat infringers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">12) Disclaimer of Warranties</h2>
                <p className="text-gray-400 leading-relaxed uppercase">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">13) Limitation of Liability</h2>
                <p className="text-gray-400 leading-relaxed uppercase">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, GOSPELTUBE INC AND ITS AFFILIATES WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
                </p>
                <p className="text-gray-400 leading-relaxed uppercase mt-4">
                  OUR TOTAL LIABILITY FOR ANY CLAIM WILL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM (OR $100 IF YOU PAID NOTHING), WHICHEVER IS GREATER.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">14) Indemnification</h2>
                <p className="text-gray-400 leading-relaxed">
                  You agree to indemnify and hold harmless GospelTube Inc and its affiliates from claims, liabilities, damages, and expenses arising from your User Content, your use of the Service, or your violation of these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">15) Termination</h2>
                <p className="text-gray-400 leading-relaxed">
                  You may stop using the Service at any time. We may suspend or terminate access at any time for violations or to protect the Service, users, and legal compliance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">16) Governing Law</h2>
                <p className="text-gray-400 leading-relaxed">
                  These Terms are governed by the laws of the State of New York, USA, excluding conflict of law rules.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">17) Changes to These Terms</h2>
                <p className="text-gray-400 leading-relaxed">
                  We may update these Terms from time to time. We will update the "Last Updated" date. Continued use after changes means you accept the updated Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">18) Contact</h2>
                <p className="text-gray-400 leading-relaxed">
                  Questions? Email <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a>.
                </p>
              </section>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} GospelTube Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-4 text-sm">
            <Link to="/terms" className="text-brand">Terms of Service</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/content-policy" className="text-gray-400 hover:text-white transition-colors">Content Policy</Link>
            <Link to="/dmca" className="text-gray-400 hover:text-white transition-colors">Copyright/DMCA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
